import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
    Account,
    AccountGroup,
    AccountGroupInfoRequest,
    DataType,
    GlobalAccount,
    GUID,
    Portfolio,
    PortfolioType,
    WashSaleGroup,
    WashSaleGroupCreateRequest,
    WashSaleGroupUpdateRequest,
} from '@ndvr/portfolio-api';

import { forgetRecentSearchEntity } from '@/components/recent-search-store';
import { PortfolioApi } from 'API';
import { Toast } from 'widgets';

export const useAccount = (
    accountGUID?: GUID<Account>,
    queryOptions?: Partial<UseQueryOptions<Account | undefined, unknown, Account | undefined, any[]>>,
) => {
    return useQuery({
        queryKey: ['account', accountGUID],
        queryFn: async () => {
            const [response] = await PortfolioApi().getAccount(accountGUID!);
            return response ?? undefined;
        },
        enabled: !!accountGUID,
        ...queryOptions,
    });
};

export const usePortfolios = (
    globalAccountGUID?: GUID<GlobalAccount>,
    type?: PortfolioType,
    isCustodyAccountLinked?: boolean,
) => {
    return useQuery({
        queryKey: [
            'globalAccount',
            globalAccountGUID,
            'portfolios',
            {
                type,
                isCustodyAccountLinked,
            },
        ],
        queryFn: async () => {
            const [response] = await PortfolioApi().getPortfolios({
                globalAccountGUID: globalAccountGUID!,
                type,
                isCustodyAccountLinked,
            });
            return response ?? undefined;
        },
        enabled: !!globalAccountGUID,
    });
};

export const usePortfolioProfiles = (portfolioGUIDs: GUID<Portfolio>[]) => {
    return useQuery({
        queryKey: ['portfolioProfiles', ...portfolioGUIDs],
        queryFn: async () => {
            const [response] = await PortfolioApi().getPortfolioProfiles(portfolioGUIDs);
            return response ?? undefined;
        },
    });
};

// TODO merge with useDeletePortfolio
export const useDeleteLiveEditAccountGroup = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (accountGroup: AccountGroup) => {
            return await PortfolioApi().deleteAccountGroup(accountGroup.accountGroupGUID);
        },
        onSuccess: ([result], data) => {
            if (result?.valid === true) {
                forgetRecentSearchEntity(data.accountGroupGUID);
            }
            Toast.success('Live edit terminated');
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', data.householdGUID],
            });
            queryClient.invalidateQueries({
                queryKey: ['accountGroup', data.accountGroupGUID],
            });
            queryClient.invalidateQueries({
                queryKey: ['accountGroup', data.originAccountGroupGUID],
            });
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to terminate live edit');
        },
    });
};

export const useWashSaleGroups = (globalAccountGUID: GUID<GlobalAccount>) => {
    return useQuery({
        queryKey: ['globalAccount', globalAccountGUID, 'washSaleGroups'],
        queryFn: async () => {
            const [response] = await PortfolioApi().getRelatedWashSaleGroups(globalAccountGUID);
            return response ?? undefined;
        },
    });
};

export const useCreateWashSaleGroup = (globalAccountGUID: GUID<GlobalAccount>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: WashSaleGroupCreateRequest) => {
            const [response] = await PortfolioApi().createWashSaleGroup(data);
            return response ?? undefined;
        },
        onSuccess: () => {
            Toast.success('Wash Sale Group created');
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', globalAccountGUID, 'washSaleGroups'],
            });
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to create Wash Sale Group');
        },
    });
};

export const useUpdateWashSaleGroup = (globalAccountGUID: GUID<GlobalAccount>) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: WashSaleGroupUpdateRequest) => {
            return PortfolioApi().updateWashSaleGroup(data);
        },
        onSuccess: () => {
            Toast.success('Wash sale group updated');
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', globalAccountGUID, 'washSaleGroups'],
            });
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to update Wash Sale Group');
        },
    });
};

export const useDeleteWashSaleGroup = (globalAccountGUID: GUID<GlobalAccount>) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (washSaleGroupGUID: GUID<WashSaleGroup>) => {
            return PortfolioApi().deleteWashSaleGroup(washSaleGroupGUID);
        },
        onSuccess: () => {
            Toast.success('Wash sale group deleted');
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', globalAccountGUID, 'washSaleGroups'],
            });
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to delete Wash Sale Group');
        },
    });
};

export const useSetAccountGroupReadyForReview = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (accountGroupGUID: GUID<AccountGroup>) =>
            PortfolioApi().setAccountGroupReadyForReview(accountGroupGUID),
        onSuccess: (_, accountGroupGUID) => {
            Toast.success('Account group status changed to ready for review');
            queryClient.invalidateQueries({
                queryKey: ['portfolio'],
            });
            queryClient.invalidateQueries({
                queryKey: ['accountGroup', accountGroupGUID],
            });
            queryClient.invalidateQueries({
                queryKey: ['globalAccount'],
            });
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to change account group status to ready for review');
        },
    });
};

export const useSetAccountGroupApproved = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (accountGroupGUID: GUID<AccountGroup>) => PortfolioApi().setAccountGroupApproved(accountGroupGUID),
        onSuccess: (_, accountGroupGUID) => {
            Toast.success('Account group approved');
            queryClient.invalidateQueries({
                queryKey: ['portfolio'],
            });
            queryClient.invalidateQueries({
                queryKey: ['accountGroup', accountGroupGUID],
            });
            queryClient.invalidateQueries({
                queryKey: ['globalAccount'],
            });
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to approve account group');
        },
    });
};

export const useApplyChangesFromEditClone = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (accountGroupGUID: GUID<AccountGroup>) =>
            PortfolioApi().applyChangesFromEditClone(accountGroupGUID),
        onSuccess: (_, accountGroupGUID) => {
            Toast.success('Account group edits applied');
            queryClient.invalidateQueries({
                queryKey: ['portfolio'],
            });
            queryClient.invalidateQueries({
                queryKey: ['accountGroup', accountGroupGUID],
            });
            queryClient.invalidateQueries({
                queryKey: ['globalAccount'],
            });
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to apply account group edits');
        },
    });
};

export const useSetAccountGroupWorkInProgress = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (accountGroupGUID: GUID<AccountGroup>) =>
            PortfolioApi().setAccountGroupWorkInProgress(accountGroupGUID),
        onSuccess: (_, accountGroupGUID) => {
            Toast.success('Account group status changed to work in progress');
            queryClient.invalidateQueries({
                queryKey: ['portfolio'],
            });
            queryClient.invalidateQueries({
                queryKey: ['accountGroup', accountGroupGUID],
            });
            queryClient.invalidateQueries({
                queryKey: ['globalAccount'],
            });
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to change account group status to work in progress');
        },
    });
};

export const useCreateAccountGroup = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            householdGUID,
            request,
        }: {
            householdGUID: GUID<GlobalAccount>;
            request: AccountGroupInfoRequest;
        }) => {
            const [response] = await PortfolioApi().createAccountGroup(householdGUID, request);
            return response ?? undefined;
        },
        onSuccess: (_, { householdGUID }) => {
            Toast.success('Account group created');
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', householdGUID],
            });
        },
        onError: (error) => Toast.forError(error, 'Failed to create account group'),
    });
};

export const useCloneAccountGroup = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            householdGUID,
            accountGroupGUID,
            request,
        }: {
            householdGUID: GUID<GlobalAccount>;
            accountGroupGUID: GUID<AccountGroup>;
            request: AccountGroupInfoRequest;
        }) => {
            const [response] = await PortfolioApi().cloneAccountGroup(householdGUID, accountGroupGUID, request);
            return response ?? undefined;
        },
        onSuccess: (_, { householdGUID }) => {
            Toast.success('Account group cloned');
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', householdGUID],
            });
        },
        onError: (error) => Toast.forError(error, 'Failed to clone account group'),
    });
};

export const useCloneAccountGroupForEdit = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (accountGroup: AccountGroup) => {
            const [response] = await PortfolioApi().cloneAccountGroupForEdit(
                accountGroup.householdGUID,
                accountGroup.accountGroupGUID,
                {
                    accountGroupName: {
                        encryptionContextGUID: accountGroup.householdGUID,
                        dataType: DataType.PORTFOLIO_NAME,
                        plainText: `${accountGroup.accountGroupName.plainText} edits`,
                    },
                    description: {
                        encryptionContextGUID: accountGroup.householdGUID,
                        dataType: DataType.PORTFOLIO_DESCRIPTION,
                        plainText: accountGroup.description?.plainText || '',
                    },
                },
            );
            return response ?? undefined;
        },
        onSuccess: (_, data) => {
            Toast.success('Account group cloned for edit');
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', data.householdGUID],
            });
            queryClient.invalidateQueries({
                queryKey: ['accountGroup', data.accountGroupGUID],
            });
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to clone account group for edit');
        },
    });
};

export const useToggleAccountTradeStatus = (householdGUID: GUID<GlobalAccount>) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (account: Account) => {
            return account.tradingSuspendedAt
                ? PortfolioApi().enableTrading(account.accountGUID)
                : PortfolioApi().suspendTrading(account.accountGUID);
        },
        onSuccess: (_, account) => {
            toast.success(`Trading ${account.tradingSuspendedAt ? 'enabled' : 'disabled'}`);
            queryClient.invalidateQueries({ queryKey: ['globalAccount', householdGUID, 'accountGroup'] });
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', householdGUID, 'accountGroupsWithAumIncluded'],
            });
            queryClient.invalidateQueries({ queryKey: ['accountGroup', account.accountGroupGUID, 'profile'] });
            queryClient.invalidateQueries({ queryKey: ['accounts', 'portfolio-engine'] });
        },
        onError: (error, account) => {
            Toast.forError(error, `Failed to ${account.tradingSuspendedAt ? 'enable' : 'disable'} trading`);
        },
    });
};

export const useDeleteAccountGroup = (householdGUID: GUID<GlobalAccount>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (accountGroupGUID: GUID<AccountGroup>) => {
            const [response] = await PortfolioApi().deleteAccountGroup(accountGroupGUID);
            return response ?? undefined;
        },
        onSuccess: (result, accountGroupGUID) => {
            if (result?.valid === true) {
                forgetRecentSearchEntity(accountGroupGUID);
            }
            queryClient.invalidateQueries({
                queryKey: ['globalAccount', householdGUID],
            });
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to delete portfolio');
        },
    });
};

export const usePortfolioBasicInformationList = (globalAccountGUID: GUID<GlobalAccount>) => {
    return useQuery({
        queryKey: ['portfolio', globalAccountGUID, 'portfolioBasicInformationList'],
        queryFn: async () => {
            const [response] = await PortfolioApi().getPortfolioBasicInformationList({ globalAccountGUID });
            return response ?? [];
        },
    });
};
